import { Checkbox, Grid } from '@mui/material';
import { RoutalButton, RoutalInputText, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Organization } from '../domain/organization/Organization';
import { useCreateWhatsAppSender } from '../hooks/whatsapp/useCreateWhatsAppSender';
import { useRetrieveMetaPhone } from '../hooks/whatsapp/useRetrieveMetaPhone';
import Text from '../react-components/Text';

/**
 * The process for registering a business acount in Twilio is the following:
 * 1. Create a WABA (WhatsApp Business Account) with the facebook Login process, it shouldn't ask for the phone (featureType: `only_waba_sharing`).
 * 2. Once created, the login returns the WABA_ID, which is used to register the WhatsApp Sender in Twilio.
 * 3. A friendlyName is sent to the backend to create a new Twilio subaccount.
 * 4. The WABA_ID is sent to the backend to register the WhatsApp Sender.
 */

interface FacebookLoginProps {
  organization: Organization;
}

export const FacebookLogin = ({ organization }: FacebookLoginProps) => {
  const [wabaId, setWabaId] = useState<string | undefined>(undefined);
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const [whatsappName, setWhatsappName] = useState<string>(``);

  const [phoneId, setPhoneId] = useState<string>(``);
  const [userCode, setUserCode] = useState<string | undefined>();
  const [authToken, setAuthToken] = useState<string | undefined>();
  const [redirectUri, setRedirectUri] = useState<string>(``);

  const [onlyWabaSharing, setOnlyWabaSharing] = useState<boolean>(true);

  const { run: createSender } = useCreateWhatsAppSender();

  const {
    data: metaPhone,
    success,
    run: retrievePhone,
  } = useRetrieveMetaPhone({
    auth_token: authToken,
    code: userCode,
    redirect_uri: redirectUri,
    phone_id: phoneId,
  });

  const isDisabled = () => !phone || !whatsappName || !wabaId;

  useEffect(() => {
    if (window.location.href) {
      setRedirectUri(window.location.href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  useEffect(() => {
    console.log(`Some of this changed`, phoneId, userCode, authToken, redirectUri);
    if (phoneId && (userCode || authToken) && redirectUri) {
      retrievePhone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId, userCode, authToken, redirectUri]);

  useEffect(() => {
    if (success && metaPhone) {
      console.log(`Meta phone`, metaPhone);
      if (phone === ``) {
        setPhone(metaPhone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      const js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.src = `https://connect.facebook.net/en_US/sdk.js`;
      js.async = true;
      js.defer = true;
      js.crossOrigin = `anonymous`;
      fjs.parentNode?.insertBefore(js, fjs);
    })(document, `script`, `facebook-jssdk`);

    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      const js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.textContent = `
      window.fbAsyncInit = function() {
        FB.init({
          appId: '575017555197023', 
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v21.0'
        });
      };
    `;
      fjs.parentNode?.insertBefore(js, fjs);
    })(document, `script`, `facebook-init`);
  }, []);

  // Handle WhatsApp Embedded Signup
  const launchEmbeddedSignup = () => {
    // Launch Facebook login
    try {
      console.log(`Getting ready to login`);

      // @ts-ignore
      FB.login(
        (response: any) => {
          console.log(`FB.login response`, response);
          const code = response.authResponse.code;
          console.log(`Code`, code);
          if (code) {
            setUserCode(code);
          }
          // Crida a Back amb el WABA_id
          // Since you are using Twilio's APIs, you do not need to do anything with the response here.
        },
        {
          config_id: `1300025577605526`,
          auth_type: `rerequest`, // Avoids "user is already logged" in errors if users click the button again before refreshing the page
          override_default_response_type: true,
          response_type: `code`,
          extras: {
            sessionInfoVersion: 3, // Required to get WABA ID
            featureType: onlyWabaSharing && `only_waba_sharing`,
            setup: {
              solutionID: `1466513314035047`,
            },
          },
        }
      );
    } catch (error) {
      console.error(`Error launching Embedded Signup:`, error);
    }
  };

  // Define session handler
  const embeddedSignupInfoListener = (event: any) => {
    if (!event.origin.endsWith(`facebook.com`)) return;
    console.log(`InfoListener event:`, event);
    try {
      const data = JSON.parse(event.data);
      console.log(`InfoListener data:`, data);
      if (data.type === `WA_EMBEDDED_SIGNUP`) {
        // if user finishes the Embedded Signup flow and the popup window closes
        if (data.event === `FINISH` || data.event === `FINISH_ONLY_WABA`) {
          const { phone_number_id, waba_id } = data.data;
          setPhoneId(phone_number_id);
          console.log(`Phone number ID `, phone_number_id, ` WhatsApp business account ID `, waba_id);
          // @ts-ignore
          FB.getLoginStatus((response) => {
            console.log(`Login Status Response`, response);
            if (response.status === `connected`) {
              const authToken = response.authResponse.accessToken;
              console.log(`Authtoken`, authToken);
              if (authToken) {
                setAuthToken(authToken);
              }
            }
          });
          // @ts-ignore
          FB.api(`/` + phone_number_id, (response: any) => {
            console.log(`API CALL RESPONSE:`, response);
            setPhone(`+${response.display_phone_number}`);
          });
          setWabaId(waba_id);
          // For now, you do not need to do anything here. Once App Review has been completed, then you
          // will pass the WABA ID to your backend service to use the Twilio Senders API to register the WhatsApp Sender.
          // Not that Meta only returns their phone_number_id, not the full phone number. Make sure to collect the
          // full phone number in your own user interface before the user clicks on this button.
        } else {
          // Otherwise the user canceled the Embedded Signup flow by closing the popup window before the last step.
          // We recommend you show an error to the user here.
          const { current_step } = data.data;
          console.log(`User did not finish ESU. Last step: ${current_step}`);
        }
      }
    } catch {
      return;
    }
  };

  // Listen for Embedded Signup events
  window.addEventListener(`message`, embeddedSignupInfoListener);

  // When the user navigates away from the page, remove the event listener
  window.addEventListener(`beforeunload`, () => window.removeEventListener(`message`, embeddedSignupInfoListener));

  return (
    <Grid width="100%">
      <Text intlId="register_whatsapp.waba_id" />
      {wabaId ? (
        <RoutalInputText fullWidth disabled fieldValue={wabaId} />
      ) : (
        <>
          <RoutalButton onClick={launchEmbeddedSignup} style={{ margin: 0 }}>
            <Text variant="button" style={{ color: RoutalPalette.neutral00 }} intlId="register_whatsapp.login" />
          </RoutalButton>
          <Grid container flexDirection="row" alignItems="center">
            <Text>Only Waba sharing</Text>
            <Checkbox checked={onlyWabaSharing} onChange={() => setOnlyWabaSharing(!onlyWabaSharing)} />
          </Grid>
        </>
      )}

      <VSpacer medium />

      <RoutalInputText
        fullWidth
        label={<Text intlId="register_whatsapp.phone" />}
        placeholder="+34666666666"
        onChange={(value) => {
          setPhone(value);
        }}
        fieldValue={phone}
      />

      <VSpacer medium />

      <RoutalInputText
        fullWidth
        label={<Text intlId="register_whatsapp.whatsapp_name" />}
        placeholder="Routal"
        onChange={(value) => {
          setWhatsappName(value);
        }}
        fieldValue={whatsappName}
      />

      <VSpacer medium />

      <Grid>
        <RoutalButton
          disabled={isDisabled()}
          onClick={() => {
            if (isDisabled()) return;
            createSender({
              subaccountName: `${organization.id}-${organization.label}`,
              phone: phone!,
              wabaId: wabaId!,
              name: whatsappName,
            });
          }}
        >
          <Text style={{ color: RoutalPalette.neutral00 }} intlId="confirm" />
        </RoutalButton>
      </Grid>
    </Grid>
  );
};
